"use client";

import React, { useState, useEffect } from "react";

const PrivacyOrganism = () => {
  const [activeSection, setActiveSection] = useState("overview");
  const [expandedItem, setExpandedItem] = useState("collection");
  const [scrollProgress, setScrollProgress] = useState(0);

  const sections = [
    {
      id: "overview",
      title: "Policy Overview",
      eyebrow: "Foundation",
      content: [
        "Tycho Technologies shall be referred to as The Company in this privacy statement.",
        "The Company is fully aware of legal obligations under applicable law and the Data Protection Act, and adheres to those obligations.",
        "Personal data is collected by Tycho only when it is absolutely necessary for technical or commercial operations.",
      ],
    },
    {
      id: "collection",
      title: "Data / Information Collection",
      eyebrow: "Section 01",
      content: [
        "Users interested in services that require registration, or users submitting online registration forms, may provide specific information by completing fields necessary for service provision.",
        "The Company informs users of data collection incidents and explains why data is being collected.",
        "The Company may track website visits to understand what users like and dislike, so services can be improved.",
        "Cookies may be installed on user devices used to access the website.",
      ],
    },
    {
      id: "control",
      title: "Control and Access to Personal Data",
      eyebrow: "Section 02",
      content: [
        "Users can view personal information stored by the Company for that user.",
        "Users can raise complaints about irrelevant, inaccurate, or out-of-date data.",
        "Users can discontinue services and request deletion of personal information from Company servers.",
      ],
    },
    {
      id: "security",
      title: "Security Commitment",
      eyebrow: "Section 03",
      content: [
        "Employees with access to user databases or service-hosting servers must adhere to security rules at all times.",
        "Sensitive and confidential data must be stored in encrypted form.",
        "The Company does not undertake that misuse, alteration, or loss of data will never occur.",
      ],
    },
    {
      id: "disclosure",
      title: "Personal Data Disclosure",
      eyebrow: "Section 04",
      content: [
        "The Company discloses user personal information only when required by law, or when disclosure is requested, authorized, and expressed by the concerned user.",
        "The Company never sells personal information to third parties unless user consent is provided.",
        "The Company stores personal information only for the duration necessary to comply with legal requirements.",
        "This Privacy Policy may change at the sole discretion of the Company without prior notification.",
      ],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.35, rootMargin: "-12% 0px -45% 0px" },
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0;
      setScrollProgress(progress);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleAccordion = (id) => {
    setExpandedItem((prev) => (prev === id ? "" : id));
  };

  return (
    <div className="bg-background-primary text-text-primary font-body min-h-screen selection:bg-bg-alt  selection:text-brand">
      <div className="fixed left-0 top-0 z-50 !h-[3px] w-full bg-brand/10">
        <div className="h-full bg-gradient-to-r from-brand to-accent transition-all duration-200" style={{ width: `${scrollProgress}%` }} />
      </div>

      <main className="container !py-14 !md:py-20">
        <header className="relative overflow-hidden rounded-3xl border border-border-primary bg-bg-alt  !px-7 !py-10 !md:px-12 !md:py-14 shadow-lg animate-fade-up">
          <div className="relative grid !gap-7 lg:grid-cols-[1.2fr_0.85fr] lg:items-end">
            <div>
              <h1 className="!mt-5 text-3xl font-display font-extrabold leading-[1.02] tracking-tight text-ink md:text-6xl">
                Privacy <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600 ">Policy</span>
              </h1>
              <p className="!mt-5 !max-w-3xl text-base leading-relaxed text-text-secondary md:text-[19px]">
                This policy explains how Tycho Technologies (The Company) handles personal data, why data is processed, and what rights users retain over their information.
              </p>
            </div>
            <div className="grid !gap-3 text-sm">
              <div className="!rounded-2xl border border-border-primary bg-surface-primary  !p-4 shadow-sm">
                <p className="font-semibold text-text-primary">Scope</p>
                <p className="!mt-1 text-text-secondary">Applies to website and service interactions.</p>
              </div>
              <div className="rounded-2xl border border-border-primary bg-surface-primary !p-4 shadow-sm">
                <p className="font-semibold text-text-primary">Core Principle</p>
                <p className="!mt-1 text-text-secondary">Data is collected only when absolutely necessary.</p>
              </div>
            </div>
          </div>
        </header>

        <div className="!mt-12 grid grid-cols-1 !gap-10 lg:grid-cols-12 xl:gap-12">
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="sticky !top-24 rounded-2xl border border-border-primary bg-surface-primary  !p-6 shadow-sm !md:p-7">
              <p className="!text-[11px] font-bold uppercase tracking-[0.2em] text-text-primary">Policy Sections</p>
              <nav className="!mt-5 grid !gap-2.5">
                {sections.map((section, index) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={() => setExpandedItem(section.id)}
                    className={`group flex items-center justify-between rounded-xl border !px-4 !py-3 text-sm transition-all duration-300 ${
                      activeSection === section.id
                        ? "border-border-primary bg-surface-secondary text-text-secondary"
                        : "border-transparent text-ink-2 hover:border-border hover:bg-bg hover:text-ink"
                    }`}
                  >
                    <span className="font-medium text-text-secondary">
                      {String(index + 1).padStart(2, "0")}. {section.title}
                    </span>
                    <span className={`h-2 w-2 rounded-full ${activeSection === section.id ? "bg-brand" : "bg-border"}`} />
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <section className="!space-y-8 lg:col-span-8 xl:col-span-9">
            {sections.map((section, index) => {
              const isExpanded = expandedItem === section.id;
              return (
                <article
                  key={section.id}
                  id={section.id}
                  className={`scroll-mt-28 overflow-hidden rounded-2xl border bg-surface-primary  transition-all duration-300 ${
                    activeSection === section.id ? "border-border-primary shadow-md" : "border-border-secondary shadow-sm hover:shadow-md"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleAccordion(section.id)}
                    className={`flex !w-full items-start justify-between !gap-6 !px-7 !py-7 text-left md:px-10! md:py-8! cursor-pointer ${
                      activeSection === section.id ? "bg-surface-primary " : "bg-background-primary "
                    }`}
                    aria-expanded={isExpanded}
                    aria-controls={`panel-${section.id}`}
                  >
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">
                        {section.eyebrow} / {String(index).padStart(2, "0")}
                      </p>
                      <h2 className="!mt-3 inline-flex rounded-md  px-2.5! py-1! font-display text-lg font-bold tracking-tight text-ink md:text-3xl">{section.title}</h2>
                    </div>
                    <div
                      className={`mt-1! flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-lg font-semibold transition-all duration-300 ${
                        isExpanded ? "border-brand/30 bg-bg-alt  text-brand" : "border-border bg-bg text-ink-2"
                      }`}
                    >
                      {isExpanded ? "-" : "+"}
                    </div>
                  </button>

                  <div id={`panel-${section.id}`} className={`grid transition-all duration-400 ease-in-out ${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <div className="border-t border-border/80 bg-bg-alt  !px-7 !pb-7 !pt-6 md:px-10! md:pb-10! md:pt-7!">
                        <ul className="!space-y-4">
                          {section.content.map((line, i) => (
                            <li key={`${section.id}-${i}`} className="flex !gap-3 rounded-lg !px-3 !py-2 text-ink-2 transition-colors duration-200 hover:bg-surface">
                              <span className="!mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand/80" />
                              <span className="leading-relaxed">{line}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}

            <div className="rounded-2xl border border-border-primary bg-surface-primary  !p-7 md:p-10">
              <h3 className="font-display text-2xl font-bold text-text-primary">Important Notice</h3>
              <p className="!mt-2 text-sm leading-relaxed text-ink-2 md:text-base">
                The Privacy Policy is subject to change at the sole discretion of The Company without prior notification. Continued use of services indicates acceptance of the
                latest policy version.
              </p>
              <p className="!mt-5 rounded-lg border border-border-primary bg-surface-primary  !px-5 !py-4 text-sm text-text-secondary shadow-sm md:text-base">
                Personal data is collected by Tycho only when it is absolutely necessary for technical/commercial operations of related activities.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyOrganism;
